import { useQuery } from '@tanstack/react-query';
import { ArrowRightMini } from '@medusajs/icons';
import { clx } from '@medusajs/ui';
import { useParams } from 'react-router-dom';

import { AppLink } from '@adapters';
import { useCartDrawer } from '@lib/context/cart-drawer-context';
import Button from '@modules/common/components/button';
import Container from '@modules/common/components/container';
import { useConfirmModalOptional } from '@lib/context/confirm-modal-context';
import { mainLayoutQueryOptions } from '@lib/query/options/layout';
import FabButton from '@modules/common/components/buttons/fab-button';
import {
  MobileNav,
  useMobileNav,
} from '@modules/layout/components/mobile-nav';
import { useMobileSearch } from '@modules/layout/components/mobile-search';
import { ProductNav } from '@modules/layout/components/product-nav';
import SearchBox from '@modules/search/components/search-box';
import PopularSearches from '@modules/search/templates/popular-searches';

import LogoBlack from './logo_black.svg';
import LogoRed from './logo_red.svg';

/**
 * Nav component - Phase 3 implementation
 * Mirrors the Next.js Nav structure with all elements
 */
export default function Nav() {
  const { countryCode = 'tw' } = useParams<{ countryCode: string }>();
  const { toggleDrawer: toggleNav } = useMobileNav();
  const { toggleDrawer: toggleSearch } = useMobileSearch();
  const { openDrawer: openCartDrawer, cartQuantity } = useCartDrawer();

  // Confirm modal for notifications (optional - may not be available yet)
  const confirmModal = useConfirmModalOptional();
  const { isPaymentConfirmed, isPriceChangedConfirmed, openModal } =
    confirmModal ?? {};
  const hasUnreadNotifications =
    confirmModal && (!isPaymentConfirmed || !isPriceChangedConfirmed);

  // Fetch layout data using React Query
  const { data } = useQuery(mainLayoutQueryOptions(countryCode));

  const {
    categories = [],
    categoryHandleConfig = { root: '', brands: '', od: '' },
    isLoggedIn = false,
    isActive = false,
    customer = null,
    navItems = [],
    alternativeSite = null,
  } = data || {};

  return (
    <>
      <MobileNav
        categories={categories}
        categoryHandleConfig={categoryHandleConfig}
        isLoggedIn={isLoggedIn}
        customer={customer}
        navItems={navItems}
      />
      <div className="bg-white-10 group sticky inset-x-0 top-0 z-40 min-h-[70px]">
        <header className="relative mx-auto bg-white duration-200">
          <nav className="h-full w-full items-center pt-2 lg:py-4">
            <Container className="flex items-center justify-between px-3 md:px-6 lg:mb-2">
              {/* Mobile: Hamburger menu */}
              <FabButton
                onClick={toggleNav}
                iconName="BarsThree"
                className="bg-transparent md:hidden"
                data-testid="nav-hamburger-button"
              />
              {/* Mobile: Search button */}
              <FabButton
                onClick={toggleSearch}
                iconName="MagnifyingGlassMini"
                className="mr-auto bg-transparent md:hidden"
                data-testid="nav-search-button"
              />

              {/* Logo - centered on mobile, left on desktop */}
              <div className="flex-auto md:flex-initial">
                <AppLink
                  href="/"
                  className="txt-compact-xlarge-plus hover:text-ui-fg-base uppercase"
                  data-testid="nav-store-link"
                >
                  {/* PharmHub logo (red) - shown by default */}
                  <img
                    className="pharmhub-show mx-auto w-[128px] md:w-[166px]"
                    src={LogoRed}
                    width={640}
                    height={160}
                    alt="logo"
                  />
                  {/* MedHub logo (black) - shown when theme-medhub */}
                  <img
                    className="medhub-show mx-auto w-[128px] md:w-[166px]"
                    src={LogoBlack}
                    width={640}
                    height={160}
                    alt="logo"
                  />
                </AppLink>
              </div>

              {/* Desktop: Search box */}
              <div className="mx-6 hidden flex-auto md:block">
                <SearchBox placeholder="請輸入商品、國際條碼或是成分搜尋" />
              </div>

              {/* Right side: Login button or user menu */}
              {isLoggedIn ? (
                <div className="mt-2 flex basis-1/12 items-center justify-end md:gap-x-6">
                  {/* Notification Icon - shown when there are unread notifications */}
                  <div
                    className={clx('invisible relative md:hidden', {
                      'visible md:block': hasUnreadNotifications,
                    })}
                  >
                    <FabButton
                      iconName="BellAlert"
                      className="md:bg-white-30 bg-transparent"
                      onClick={openModal}
                      data-testid="nav-notification-bell"
                    />
                    {/* Red dot indicator */}
                    <div
                      className="bg-red-20 absolute right-3 top-3 h-1 w-1 rounded-full"
                      data-testid="nav-notification-bell-unread-dot"
                    />
                  </div>

                  <AppLink
                    href="/account"
                    data-testid="nav-account-link"
                    className="hidden md:block"
                  >
                    <FabButton iconName="User" />
                  </AppLink>
                  {isActive && (
                    <button
                      type="button"
                      onClick={openCartDrawer}
                      data-testid="nav-cart-link"
                      className="relative"
                    >
                      <FabButton
                        iconName="ShoppingBag"
                        className="md:bg-white-30 bg-transparent"
                      />
                      {cartQuantity > 0 && (
                        <span className="bg-red-20 text-white-10 absolute -right-2 -top-3 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-bold">
                          {cartQuantity > 999 ? '999+' : cartQuantity}
                        </span>
                      )}
                    </button>
                  )}
                  {alternativeSite && (
                    <a
                      href={alternativeSite.url}
                      className={clx(
                        'group/nav-btn hidden cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-base font-bold text-white-10 md:flex',
                        {
                          'bg-beige-50 hover:bg-beige-40':
                            alternativeSite.theme === 'medhub',
                          'bg-red-50 hover:bg-red-40':
                            alternativeSite.theme === 'pharmhub',
                        },
                      )}
                    >
                      {alternativeSite.label}
                      <ArrowRightMini className="hidden group-hover/nav-btn:block" />
                    </a>
                  )}
                </div>
              ) : (
                <div>
                  <Button as="link" href="/account" className="mx-6 md:mx-0" data-testid="nav-login-button">
                    會員登入
                  </Button>
                </div>
              )}
            </Container>

            {/* Desktop: Popular searches */}
            <div className="mb-1 hidden md:block">
              <PopularSearches />
            </div>

            {/* Desktop: Product navigation */}
            <ProductNav
              categories={categories}
              categoryHandleConfig={categoryHandleConfig}
            />
          </nav>
        </header>
      </div>
    </>
  );
}
